import React from "react";
import PropTypes from "prop-types";
import Ripple from "./ripple";
import styled from "styled-components";
import { theme } from "../theme";
import { rgba } from "polished";
import { Flex } from "./layout";

// Define the ButtonStyle with size variants
const ButtonStyle = styled(({ component: Element = "button", ...props }) => (
  <Element {...props} />
))((props) => {
  const {
    color,
    Colorvariant = "main",
    variant = "text",
    fullWidth,
    sx = {},
    disabled,
    loading,
    size = "md",
  } = props;

  const colorObj = theme.colors[color] || theme.colors.main; // Default to dark theme colors
  const bgColorValue = colorObj[Colorvariant] || colorObj.main; // Background color
  const colorLight = rgba(bgColorValue, 0.2); // Lightened version of the background color
  let backgroundColor,
    textColor,
    padding,
    border,
    fontSize,
    iconSize,
    lineHeight;

  // Size variations
  switch (size) {
    case "sm":
      padding =   "0 0.75rem";
      fontSize = "0.8125rem";
      iconSize = "1.125rem";
      lineHeight = variant === "outlined" ? "1.9375rem" : "2.0625rem";
      break;
    case "lg":
      padding =   "0 1.5rem";
      fontSize = "1.25rem";
      iconSize = "1.875rem";
      lineHeight = variant === "outlined" ? "3.125rem" : "3.25rem";
      break;
    default: // medium
      padding =   "0 1.25rem";
      fontSize = "0.875rem";
      iconSize = "1.5rem";
      lineHeight = variant === "outlined" ? "2.375rem" : "2.5rem";
  }

  // Switch statement for variant
  switch (variant) {
    case "outlined":
      backgroundColor = "transparent"; // Transparent background for outlined variant
      border = `${rgba(bgColorValue, 1)} solid 0.0625rem`; // Border color matches text color
      break;
    case "filled":
      backgroundColor = colorLight; // Regular background for filled variant
      border = "transparent"; // No visible border for filled variant
      break;
    case "contained":
      backgroundColor = bgColorValue; // Regular background for contained variant
      border = "transparent"; // No visible border for contained variant
      break;
    default:
      backgroundColor = "transparent"; // Default to regular background color
      border = "transparent"; // Default border
  }

  // Adjust text color based on the combination of color and variant
  switch (color) {
    case "primary":
      textColor =
        variant === "outlined" || variant === "filled" || variant === "text"
          ? theme.colors.primary.main
          : theme.colors.light.light;
      break;
    case "secondary":
      textColor =
        variant === "outlined" || variant === "filled" || variant === "text"
          ? theme.colors.secondary.main
          : theme.colors.light.light;
      break;
    case "success":
      textColor =
        variant === "outlined" || variant === "filled" || variant === "text"
          ? theme.colors.success.main
          : theme.colors.light.light;
      break;
    case "danger":
      textColor =
        variant === "outlined" || variant === "filled" || variant === "text"
          ? theme.colors.danger.main
          : theme.colors.light.light;
      break;
    case "warning":
      textColor =
        variant === "outlined" || variant === "filled" || variant === "text"
          ? theme.colors.warning.main
          : theme.colors.dark.main;
      break;
    case "info":
      textColor =
        variant === "outlined" || variant === "filled" || variant === "text"
          ? theme.colors.info.main
          : theme.colors.light.light;
      break;
    case "light":
      textColor =
        variant === "outlined" || variant === "filled" || variant === "text"
          ? theme.colors.light.main
          : theme.colors.dark.light;
      break;
    case "dark":
      textColor =
        variant === "outlined" || variant === "filled" || variant === "text"
          ? theme.colors.dark.main
          : theme.colors.light.light;
      break;
    default:
      textColor = theme.colors.default.dark;
  }

  return {
    display: "flex",
    alignItems: "center",
    width: fullWidth ? "100%" : "auto",
    textAlign: "center",
    justifyContent: "center",
    backgroundColor,
    color: textColor,
    fontStyle: "normal",
    cursor: disabled || loading ? "not-allowed" : "pointer",
    padding,
    borderRadius: theme.radius.element,
    fontSize,
    fontWeight: 500,
    lineHeight:lineHeight,
    outline: "none",
    border,
    overflow: "hidden",
    position: "relative",
    textTransform: "capitalize",
    opacity: disabled || loading ? 0.6 : 1,
    fontFamily: theme.typography.fontFamily,
    pointerEvents: disabled || (loading && "none"),

    "& .iconLoading": {
      width: `calc(${iconSize} + (${iconSize}/3) )`,
      height: `calc(${iconSize} + (${iconSize}/3) )`,
      alignItems: "center",
      justifyContent: "center",
      marginInlineStart: "0.25rem",
      marginInlineEnd: "-1rem",
      position: "relative",
      transform: `translateY(calc(${iconSize}/3))`,
      color: textColor,
      fill: textColor,
    },
    "& .iconBox": {
      width: iconSize,
      height: iconSize,
      alignItems: "center",
      justifyContent: "center",
      marginInlineEnd: "0.5rem",
      // marginInlineStart: "-0.5rem",
      position: "relative",
      color: textColor,
      fill: textColor,
    },
    "&:hover": {
      backgroundColor: disabled
        ? backgroundColor
        : variant === "outlined"
          ? rgba(bgColorValue, 0.15) // Light hover for outlined
          : variant === "filled"
            ? rgba(bgColorValue, 0.35) // Darker for filled
            : variant === "contained"
              ? rgba(bgColorValue, 0.9) // Light hover for contained
              : rgba(bgColorValue, 0.1), // Default for text variant
    },
    ...sx,
  };
});

// Define your button component that uses the  ButtonStyle
export const Button = ({
  children,
  icon,
  color = "default",
  fullWidth,
  className,
  disabled,
  loading,
  onClick,
  Colorvariant,
  variant = "text",
  sx,
  size = "medium",
}) => {
  const colorObj = theme.colors[color] || theme.colors.dark; // Default to dark theme colors
  let rippleColor; // Extract the light version of the color for ripple

  // Determine ripple color based on variant
  if (variant === "outlined" || variant === "text" || variant === "filled") {
    rippleColor = colorObj.light; // Use light color for outlined and text variants
  } else {
    rippleColor = theme.colors.light.light; // Light text for filled or contained
  }

  return (
    <ButtonStyle
      color={color}
      Colorvariant={Colorvariant}
      disabled={disabled}
      loading={loading}
      onClick={onClick}
      className={`${className}${loading ? "loading" : ""}`}
      variant={variant}
      fullWidth={fullWidth}
      sx={sx}
      size={size} // Added size prop
    >
      {icon && !loading && <Flex className="iconBox">{icon}</Flex>}
      {loading ? (
        <>
          {`Loading`}
          <Flex className="iconLoading">
            <svg viewBox="0 0 100 100">
              <circle stroke="none" cx="6" cy="50" r="6">
                <animate
                  attributeName="opacity"
                  dur="1s"
                  values="0;1;0"
                  repeatCount="indefinite"
                  begin="0.1"
                />
              </circle>
              <circle stroke="none" cx="26" cy="50" r="6">
                <animate
                  attributeName="opacity"
                  dur="1s"
                  values="0;1;0"
                  repeatCount="indefinite"
                  begin="0.2"
                />
              </circle>
              <circle stroke="none" cx="46" cy="50" r="6">
                <animate
                  attributeName="opacity"
                  dur="1s"
                  values="0;1;0"
                  repeatCount="indefinite"
                  begin="0.3"
                />
              </circle>
            </svg>
          </Flex>
        </>
      ) : (
        children
      )}
      <Ripple color={rippleColor} duration={1000} />
    </ButtonStyle>
  );
};

Button.propTypes = {
  /** The content of the button */
  children: PropTypes.node.isRequired,

  /** The icon to display inside the button */
  icon: PropTypes.node,

  /** The color theme for the button (e.g., 'primary', 'secondary', etc.) */
  color: PropTypes.oneOf([
    "default",
    "primary",
    "secondary",
    "success",
    "danger",
    "warning",
    "info",
    "light",
    "dark",
  ]),

  /** The color variant for the button background (e.g., 'main', 'light', 'dark') */
  Colorvariant: PropTypes.oneOf(["main", "light", "dark"]),

  /** Whether the button should take up the full width of its parent */
  fullWidth: PropTypes.bool,

  /** Additional CSS classes for the button */
  className: PropTypes.string,

  /** If true, the button will be disabled */
  disabled: PropTypes.bool,

  /** If true, the button will show a loading indicator */
  loading: PropTypes.bool,

  /** The variant style of the button (e.g., 'text', 'outlined', 'filled', 'contained') */
  variant: PropTypes.oneOf(["text", "outlined", "filled", "contained"]),

  /** Inline styling for the button */
  sx: PropTypes.object,

  /** Function to call when the button is clicked */
  onClick: PropTypes.func,

  /** Size of the button (e.g., 'sm', 'medium', 'lg') */
  size: PropTypes.oneOf(["sm", "medium", "lg"]),
};

Button.defaultProps = {
  color: "default",
  Colorvariant: "main",
  fullWidth: false,
  className: "",
  disabled: false,
  loading: false,
  variant: "text",
  sx: {},
  onClick: () => {},
  size: "medium",
};

// ButtonGroup component with size prop passed to children
const ButtonGroupStyle = styled("div")(({
  variant,
  color,
  align,
  size,
  direction,
}) => {
  const colorObj = theme.colors[color] || theme.colors.default; // Default to primary if color not found
  const separatorColor = colorObj.main; // Use the main color variant
  let groupPadding;

  switch (size) {
    case "sm":
      groupPadding = "0 0.625rem";
      break;
    case "lg":
      groupPadding = "0 1.25rem";
      break;
    default: // medium
      groupPadding = "0 1rem";
  }
  return {
    display: "flex",
    justifyContent:
      align === "center"
        ? "center"
        : align === "right"
          ? "flex-end"
          : "flex-start",
    flexDirection: direction === "column" ? "column" : "row",
    // gap: spacing === 'sm' ? '0.25rem' : spacing === 'medium' ? '0.5rem' : spacing === 'lg' ? '1rem' : '0',

    [`${ButtonStyle}`]: {
      borderRadius: 0, // Remove border-radius for middle buttons in the group
      position: "relative", // Ensure relative positioning for the divider
      padding: groupPadding,

      ...(variant === "outlined"
        ? {
            borderInline: "none", // Add left border for the last child
          }
        : {}),

      "&:first-child": {
        borderRadius: `${theme.radius.element} 0 0 ${theme.radius.element}`,
        ...(variant === "outlined" && {
          borderLeft: `${separatorColor} solid 0.0625rem`, // Remove right border for first button in outlined variant
        }),
      },

      "&:last-child": {
        borderRadius: `0 ${theme.radius.element} ${theme.radius.element} 0`,
        ...(variant === "outlined" && {
          borderRight: `${separatorColor} solid 0.0625rem`, // Remove right border for first button in outlined variant
        }),
      },

      "&:not(:last-child):after": {
        content: '""',
        position: "absolute",
        width: "1px",
        height: "100%",
        backgroundColor: separatorColor, // Separator color
        right: 0, // Adjust the positioning of the separator
        top: 0,
      },
    },
  };
});

export const ButtonGroup = ({
  children,
  variant,
  color,
  align,
  spacing,
  direction,
  className,
  size,
}) => {
  return (
    <ButtonGroupStyle
      variant={variant}
      color={color}
      align={align}
      spacing={spacing}
      direction={direction}
      className={className}
      size={size}
    >
      {React.Children.map(
        children,
        (child) => React.cloneElement(child, { variant, color, size }) // Pass size to each child button
      )}
    </ButtonGroupStyle>
  );
};

// PropTypes for the ButtonGroup component
// PropTypes for the ButtonGroup component
ButtonGroup.propTypes = {
  /** The buttons to be grouped together */
  /** The buttons to be grouped together */
  children: PropTypes.node.isRequired,

  /** The variant style of the buttons in the group */
  variant: PropTypes.oneOf(["text", "outlined", "filled", "contained"]),

  /** The color of the buttons in the group */
  color: PropTypes.oneOf([
    "default",
    "primary",
    "secondary",
    "success",
    "danger",
    "warning",
    "info",
    "light",
    "dark",
  ]),

  /** The alignment of the button group (left, center, right) */
  align: PropTypes.oneOf(["left", "center", "right"]),

  /** The spacing between buttons (sm, medium, lg) */
  spacing: PropTypes.oneOf(["sm", "medium", "lg"]),

  /** The direction of the button group (row or column) */
  direction: PropTypes.oneOf(["row", "column"]),

  /** Additional class names for styling */
  /** Additional class names for styling */
  className: PropTypes.string,

  /** Size of the buttons in the group (e.g., 'sm', 'medium', 'lg') */
  size: PropTypes.oneOf(["sm", "medium", "lg"]),
};

ButtonGroup.defaultProps = {
  variant: "text",
  color: "default",
  align: "left",
  spacing: "medium",
  direction: "row",
  size: "medium", // Default size
};